import { Box, Chip, Stack, Typography } from '@mui/material';
import { useTheme, type Theme } from '@mui/material/styles';

import { theme as llamaBaseTheme } from '@karehero/llama/theme/mui';

import {
  Frame,
  FrameGrid,
  LibraryShell,
} from '@/designs-dev/library/LibraryCanvas';
import { prototypeThemeOverrides } from '@/theme';

// What the theme does to each MUI component, read live from both layers: llama's
// theme, and the overrides Loft lays on top (theme.tsx). A Loft entry is a
// proposal, not yet in llama.

type Layer = 'llama' | 'Loft';
type Override = {
  defaultProps?: Record<string, unknown>;
  styleOverrides?: Record<string, unknown>;
  variants?: { props?: Record<string, unknown>; style?: unknown }[];
};

const LAYERS: { layer: Layer; components: Record<string, Override> }[] = [
  {
    layer: 'llama',
    components: (llamaBaseTheme.components ?? {}) as Record<string, Override>,
  },
  {
    layer: 'Loft',
    components: (prototypeThemeOverrides ?? {}) as Record<string, Override>,
  },
];

const KEYS = [
  ...new Set(LAYERS.flatMap((l) => Object.keys(l.components))),
].sort((a, b) => a.localeCompare(b));

// A function override is worked out when it renders, so run it here against the
// live theme, with the variant's props as the component's. One that returns
// nothing without other props is said to depend on them rather than shown empty.
const show = (
  v: unknown,
  theme: Theme,
  props: Record<string, unknown> = {},
): string => {
  const run = (val: unknown): unknown => {
    if (typeof val !== 'function') return val;
    try {
      return val({ theme, ownerState: props, ...props });
    } catch {
      return undefined;
    }
  };
  const value = run(v);
  if (
    value === undefined ||
    (typeof value === 'object' &&
      value !== null &&
      Object.keys(value).length === 0 &&
      typeof v === 'function')
  )
    return 'Set by a function that depends on props, so nothing to show here';
  return JSON.stringify(
    value,
    (_k, val) => (typeof val === 'function' ? (run(val) ?? 'ƒ') : val),
    2,
  );
};

// Loft carries llama's variants over when it adds one of its own (an array in an
// override replaces the one under it), so those copies are llama's, not Loft's.
const inLlama = (muiKey: string, v: { props?: unknown }) =>
  (LAYERS[0].components[muiKey]?.variants ?? []).some(
    (l) => JSON.stringify(l.props) === JSON.stringify(v.props),
  );

const propsLabel = (props?: Record<string, unknown>) =>
  Object.entries(props ?? {})
    .map(([k, v]) => `${k}='${v}'`)
    .join(', ') || 'Every instance';

const Code = ({ children }: { children: string }) => (
  <Box
    component='pre'
    sx={{
      m: 0,
      p: 1.5,
      maxWidth: 480,
      maxHeight: 320,
      overflow: 'auto',
      bgcolor: 'custom.neutral.100',
      borderRadius: 1,
    }}
  >
    <Typography variant='mono' sx={{ whiteSpace: 'pre' }}>
      {children}
    </Typography>
  </Box>
);

/** One thing the theme sets, labelled with where it comes from. */
const Entry = ({
  layer,
  label,
  value,
  props,
}: {
  layer: Layer;
  label: string;
  value: unknown;
  props?: Record<string, unknown>;
}) => {
  const theme = useTheme();
  return (
    <Stack spacing={1}>
      <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
        <Chip
          size='small'
          label={layer === 'Loft' ? 'Loft, proposed' : 'llama'}
          color={layer === 'Loft' ? 'secondary' : 'default'}
        />
        <Typography variant='subtitle2'>{label}</Typography>
      </Stack>
      <Code>{show(value, theme, props)}</Code>
    </Stack>
  );
};

const ComponentTheme = ({ muiKey }: { muiKey: string }) => (
  <Stack spacing={3}>
    {LAYERS.map(({ layer, components }) => {
      const o = components[muiKey];
      if (!o) return null;
      return (
        <Stack key={layer} spacing={2}>
          {o.defaultProps && (
            <Entry layer={layer} label='Default props' value={o.defaultProps} />
          )}
          {Object.entries(o.styleOverrides ?? {}).map(([slot, style]) => (
            <Entry
              key={slot}
              layer={layer}
              label={`Style: ${slot}`}
              value={style}
            />
          ))}
          {(o.variants ?? [])
            .filter((v) => !(layer === 'Loft' && inLlama(muiKey, v)))
            .map((v, i) => (
              <Entry
                key={i}
                layer={layer}
                label={`Variant: ${propsLabel(v.props)}`}
                value={v.style}
                props={v.props}
              />
            ))}
        </Stack>
      );
    })}
  </Stack>
);

/** Theme: every component the theme touches, and what it sets on each. */
const ThemeCanvas = () => (
  <LibraryShell title='Theme'>
    <FrameGrid>
      {KEYS.map((key) => (
        <Frame key={key} title={key.replace(/^Mui/, '')}>
          <ComponentTheme muiKey={key} />
        </Frame>
      ))}
    </FrameGrid>
  </LibraryShell>
);

export default ThemeCanvas;
